import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import { SortableHandle, SortableElement } from 'react-sortable-hoc';

import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';

import { CloseMenu } from '@webapp/store/ui/action-creators';

import { Option } from '@webapp/types/common/option';

import { Collaborator } from '@services/collaborator/types/collaborator';

import Menu from '@components/menu';
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import CloseIcon from '@components/basics/icons/close';
import Typography from '@components/basics/typography';
import HandleIcon from '@components/basics/icons/handle';
import MotionOpacity from '@components/motion/motion-opacity';
import DotsCircleIcon from '@components/basics/icons/dots-circle';

import InputTabSelector from '@webapp/controllers/input-tab-selector';
import ArtistDropdownRole from '@webapp/controllers/artist/artist-dropdown-role';

interface CollaboratorRowCtrlProps {
  className?: string;
  firstBigger?: boolean;
  inHalf?: boolean;
  collaborator: Collaborator;
  roleList: Option[];
  isDisabled: boolean;
  hasMultiSelect?: boolean;
  isActiveArtist?: boolean;
  hasTabSelector?: boolean;
  onRemove: () => void;
  closeMenu: () => CloseMenu;
  onChangeRole: (value: string) => void;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
});

export const CollaboratorRowCtrl: React.FC<CollaboratorRowCtrlProps> = (
  props
) => {
  const [isActive, setActive] = React.useState(false);
  const [showRemove, setShowRemove] = React.useState(false);
  const [isMenuActive, setMenuActive] = React.useState(false);

  const rightDecoration = (
    <Menu
      className="menu"
      button={<DotsCircleIcon className="open-icon" />}
      closeButton={
        <CloseIcon onClick={props.closeMenu} className="close-icon" />
      }
      customIconHeight="24px"
      menuId={`COLLABORATORS-MENU-${props.collaborator.collaboratorId}`}
      onClick={() => setMenuActive(true)}
    >
      <MenuContent>
        <Typography
          weight="700"
          className="menu-item danger"
          onClick={() => setShowRemove(true)}
        >
          <Translate id="RELEASE.TRACK_INFO.TRACK.COLLABORATOR.REMOVE" />
        </Typography>
      </MenuContent>
    </Menu>
  );

  return (
    <Translate>
      {({ translate }) => (
        <div
          className={props.className}
          onMouseOver={() => setActive(true)}
          onMouseLeave={() => setActive(false)}
        >
          <AnimatePresence>
            {isActive && <Handle className="icon-handle" />}
          </AnimatePresence>
          <Wrapper>
            <Flex justifyContent="space-between" alignItems="center" gap={3}>
              <InputField
                className={`collaborator-input-field ${
                  isMenuActive ? 'parent-menu-open' : ''
                }`}
                label={
                  props.collaborator.type === "CONTRIBUTOR" ?
                  <Translate id="RELEASE.TRACK_INFO.TRACK.CREDIT.LABEL" />: 
                  <Translate id="RELEASE.TRACK_INFO.TRACK.COLLABORATOR.LABEL" />
                }
                value={
                  props.isActiveArtist
                    ? translate('RELEASE.TRACK_INFO.TRACK.COLLABORATOR.YOU', {
                        artistName: props.collaborator.name || '',
                      })
                    : props.collaborator.name || ''
                }
                locked={true}
                customBorderRadius="4px 4px 0 0"
                hasTruncate
                rightDecoration={!props.isDisabled && rightDecoration}
              />
              {props.hasTabSelector ? (
                <InputTabSelector
                  value={props.collaborator.role}
                  options={props.roleList}
                  isDisabled={props.isDisabled}
                  onClick={(value) => props.onChangeRole(value)}
                />
              ) : null}
            </Flex>
            {!props.hasTabSelector ? (
              <ArtistDropdownRole
                className="artist-dropdown-role"
                value={props.collaborator.role}
                roleList={props.roleList}
                isDisabled={props.isDisabled}
                hasMultipleSelect={props.hasMultiSelect}
                customBorderRadius={showRemove ? '0' : undefined}
                onChange={(value) => props.onChangeRole(value)}
              />
            ) : null}
            <AnimatePresence exitBeforeEnter>
              {showRemove && (
                <MotionOpacity>
                  <RemoveWrapper>
                    <Typography>
                      <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE" />
                    </Typography>
                    <Flex justifyContent="flex-end" alignItems="center" gap={8}>
                      <Link className="red" onClick={() => props.onRemove()}>
                        <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE.YES" />
                      </Link>
                      <Link onClick={() => setShowRemove(false)}>
                        <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE.NO" />
                      </Link>
                    </Flex>
                  </RemoveWrapper>
                </MotionOpacity>
              )}
            </AnimatePresence>
          </Wrapper>
        </div>
      )}
    </Translate>
  );
};

export default SortableElement(
  connect(
    null,
    mapDispatchToProps
  )(styled(CollaboratorRowCtrl)`
    position: relative;
    margin-top: 10px;

    .decoration-right {
      z-index: 99999;
    }

    .menu {
      .open-icon,
      .close-icon {
        width: 24px;
        height: 24px;
        cursor: pointer;
      }
    }

    .artist-dropdown-role {
      position: relative;
    }
  `)
);

const Wrapper = styled.div`
  display: grid;
  grid-gap: 3px;

  .collaborator-input-field {
    flex-grow: 1;
    .decoration-right {
      z-index: 0;
    }
    &.parent-menu-open {
      .decoration-right {
        z-index: 2;
      }
    }
  }
`;

const Handle = SortableHandle(styled((props: { className?: string }) => (
  <HandleIcon {...props} />
))`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  z-index: 4;
`);

const MenuContent = styled.div`
  display: grid;
  grid-gap: 5px;
  min-width: 128px;
  z-index: 9999;

  .menu-item {
    cursor: pointer;
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:hover {
      color: ${({ theme }) => theme.colors.tertiary};
    }

    &.danger {
      color: ${({ theme }) => theme.colors.accent};
      &:hover {
        color: ${({ theme }) => theme.colors.accentHover};
      }
    }
  }
`;

const RemoveWrapper = styled.div`
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 0px 0px 4px 4px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.accentLight};

  @media ${({ theme }) => theme.device.laptopL} {
    padding: 10px 20px;
  }

  span {
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
  }
`;

const Link = styled(Typography)`
  cursor: pointer;
  opacity: 1;
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.5;
  }

  &.red {
    color: ${({ theme }) => theme.colors.accent};
  }
`;
